<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcProductGeneral',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    product: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const ux = ref([]);

  const $data = computed(() => ({
    ux: ux.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'ux' }],
    loaders: {},
    ux: {},
    dataRefs: { ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.product,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('product');
    }
  );

  const act_2095089031469157_emit_mho1r4m6p_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const inst_evh_2095089031469157_close_aj57t5ujf_getWhen = async ($event) => {
    return !!$event.data.modalResult.value;
  };
  const act_2095089031469157_showModal_w5wsotzkz_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'ProductEditor',
      props: {
        target: 'general',
        product: _.cloneDeep(props.product),
      },
    };
  };

  const act_7315092382398562_reloadComponentData_zz5eyroo4 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_8laj4jags =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_zz5eyroo4,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_8laj4jags],
    event: 'reload',
  });
  const act_2095089031469157_emit_mho1r4m6p = new actionTypes.EmitAction({
    actionArgs: act_2095089031469157_emit_mho1r4m6p_getActionArgs,
  });
  const inst_evh_2095089031469157_close_aj57t5ujf =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_emit_mho1r4m6p,
      event: 'close',
      when: inst_evh_2095089031469157_close_aj57t5ujf_getWhen,
      displayName: 'emit',
    });
  const act_2095089031469157_showModal_w5wsotzkz =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_w5wsotzkz_getActionArgs,
      events: [inst_evh_2095089031469157_close_aj57t5ujf],
    });
  const inst_evh_2095089031469157_clickPreventStop_ny4vottlc =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_w5wsotzkz,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_ny4vottlc],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_8laj4jags,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_aj57t5ujf,
    inst_evh_2095089031469157_clickPreventStop_ny4vottlc,
    inst_evh_2095089031469157_clickPreventStop,
  };

  const evh_7315092382398562_reload_8laj4jags = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_8laj4jags.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_aj57t5ujf = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_aj57t5ujf.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_ny4vottlc = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_ny4vottlc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ pgettext('Product', 'General') }}
          </strong>
          <ItpButton
            :text="gettext('Edit...')"
            variant="light"
            size="sm"
            class="ms-auto"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Type')"
          :content="pgettext('ProductTypes', product.product_type, 'Sentence')"
          v-if="product.product_type"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Number')"
          :content="product.number"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Name')"
          :content="product.name"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Description')"
          :content="product.description"
          v-if="product.description"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Unit')"
          v-if="product.unit"
        >
          <ItpText>
            {{ product.unit }}
          </ItpText>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem :label="pgettext('Product', 'VAT codes')">
          <div>
            <ItpText muted>{{ pgettext('Product', 'Sales VAT') }}:</ItpText>
            {{ product.sales_vat_code }}
          </div>
          <div>
            <ItpText muted>{{ pgettext('Product', 'Purchase VAT') }}:</ItpText>
            {{ product.purchase_vat_code }}
          </div>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Invoice group')"
          v-if="product.invoice_group_id"
        >
          <UcInvoiceGroup :groupId="product.invoice_group_id"> </UcInvoiceGroup>
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Intermediated service')"
          :content="gettext('Yes')"
          v-if="product.intermediated_service"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'Customs tariff no.')"
          :content="product.customs_tariff_no"
          v-if="product.customs_tariff_no"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'TESZOR code (HU)')"
          :content="product.hu_teszor_code"
          v-if="product.hu_teszor_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'EWC code (EU)')"
          :content="product.eu_ewc_code"
          v-if="product.eu_ewc_code"
        >
        </ItpLabeledListGroupItem>
        <ItpLabeledListGroupItem
          :label="pgettext('Product', 'External key')"
          :content="product.external_key"
          v-if="product.external_key"
        >
        </ItpLabeledListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
